import React from "react";
import {
  Stack,
  Text,
  Group,
  Paper,
  Divider,
  List,
  ThemeIcon,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons";
import moment from "moment";
import McaStatusBadge from "./McaStatusBadge";
import McaApprovalForm from "./McaApprovalForm";
import McaStepFieldsInput from "./McaStepFieldsInput";
import {
  migrateStepFields,
  isFileFieldType,
  formatStepFieldLabel,
  formatStepFieldValue,
} from "../../data/mcaEntities";
import { useFormatter } from "../../hooks/useFomatter";

const formatApprover = (user, formatter) => {
  if (!user) return null;
  if (typeof user === "object") {
    const name =
      formatter.friendlyName(user) || user.cname || user.name || null;
    if (name) {
      return user.staffId ? `${name} (${user.staffId})` : name;
    }
    if (user._id) return String(user._id);
    return null;
  }
  return String(user);
};

const McaStepDetail = ({
  request,
  process,
  selectedStep,
  canApprove,
  canUpload,
  loading,
  uploading,
  onApprove,
  onReject,
  onTerminate,
  onUploadField,
}) => {
  const [formatter] = useFormatter();
  const sortedSteps = (process?.steps ?? [])
    .slice()
    .sort((a, b) => a.order - b.order);
  const stepDef = sortedSteps[selectedStep];
  const stepResult = request?.stepResults?.[selectedStep];
  const isCurrentStep = selectedStep === (request?.currentStep ?? 0);
  const isActive = ["PENDING", "IN_PROGRESS"].includes(request?.status);
  const isRejectedResubmitStep =
    request?.status === "REJECTED" &&
    selectedStep === (request?.lastRejection?.step ?? request?.currentStep ?? 0);
  const showApprovalForm = isCurrentStep && canApprove && isActive;
  const hideReadOnlyStepContent = showApprovalForm || (isCurrentStep && isActive);

  const stepFieldDefs = migrateStepFields(stepDef ?? {});
  const stepFields =
    (stepResult?.stepFields ?? []).length > 0
      ? stepResult.stepFields
      : stepFieldDefs.map((item) => ({
          label: item.label,
          cname: item.cname,
          inputType: item.inputType ?? "TEXT",
          required: !!item.required,
          value: isFileFieldType(item.inputType) ? [] : null,
        }));
  const fileFieldsWithIndex = stepFields
    .map((field, idx) => ({ field, idx }))
    .filter(({ field }) => isFileFieldType(field.inputType));
  const hasFileFields = fileFieldsWithIndex.length > 0;
  const canUploadHere =
    canUpload && (isActive ? isCurrentStep : isRejectedResubmitStep);

  if (!stepDef && !stepResult) {
    return (
      <Text color="dimmed" size="sm">
        Select a step to view details.
      </Text>
    );
  }


  return (
    <Paper withBorder p="lg" radius="sm">
      <Stack spacing="md">
        <Group position="apart">
          <Text weight={700} size="lg">
            {stepDef?.name ?? stepResult?.name ?? `Step ${selectedStep + 1}`}
          </Text>
          {stepResult?.status && (
            <McaStatusBadge status={stepResult.status} />
          )}
        </Group>

        {stepDef?.description && (
          <Text size="sm" color="dimmed">
            {stepDef.description}
          </Text>
        )}

        {stepResult?.actionAt && (
          <>
            <Divider />
            <Stack spacing="xs">
              <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                Action
              </Text>
              <Text size="sm">
                <Text component="span" weight={600}>
                  By:
                </Text>{" "}
                {formatApprover(stepResult.actionBy, formatter) ?? "—"}
              </Text>
              <Text size="sm">
                <Text component="span" weight={600}>
                  At:
                </Text>{" "}
                {moment(stepResult.actionAt).format("YYYY-MM-DD HH:mm")}
              </Text>
              {stepResult.comment && (
                <Text size="sm">
                  <Text component="span" weight={600}>
                    {stepResult.status === "REJECTED"
                      ? "Rejection reason:"
                      : "Comment:"}
                  </Text>{" "}
                  {stepResult.comment}
                </Text>
              )}
            </Stack>
          </>
        )}

        {!hideReadOnlyStepContent && (stepResult?.checklist ?? []).length > 0 && (
          <>
            <Divider />
            <Stack spacing="xs">
              <Text size="xs" color="dimmed" weight={600}>
                Check List Items
              </Text>
              <Divider />
              <List spacing="xs" size="sm">
                {stepResult.checklist.map((item, idx) => (
                  <List.Item
                    key={idx}
                    icon={
                      <ThemeIcon
                        color={item.checked ? "green" : "gray"}
                        size={20}
                        radius="xl"
                        variant={item.checked ? "filled" : "outline"}
                      >
                        {item.checked ? (
                          <IconCheck size={12} />
                        ) : (
                          <IconX size={12} />
                        )}
                      </ThemeIcon>
                    }
                  >
                    {item.label}
                    {item.required ? " *" : ""}
                  </List.Item>
                ))}
              </List>
            </Stack>
          </>
        )}

        {!hideReadOnlyStepContent && (stepResult?.stepFields ?? []).length > 0 && (
          <>
            <Divider />
            <Stack spacing="xs">
              <Text size="xs" color="dimmed" weight={600}>
                Form
              </Text>
              <Divider />
              {stepResult.stepFields.map((field, idx) => (
                <Text key={idx} size="sm">
                  <Text component="span" weight={600}>
                    {formatStepFieldLabel(field)}:
                  </Text>{" "}
                  {formatStepFieldValue(field)}
                </Text>
              ))}
            </Stack>
          </>
        )}

        {hasFileFields && !hideReadOnlyStepContent && (
          <>
            <Divider />
            <Stack spacing="sm">
              <Text size="xs" color="dimmed" weight={600}>
                Form
              </Text>
              <Divider />
              <McaStepFieldsInput
                fields={fileFieldsWithIndex.map(({ field }) => field)}
                canUpload={canUploadHere}
                onUploadField={(localIdx, file) =>
                  onUploadField?.(fileFieldsWithIndex[localIdx]?.idx, file)
                }
                uploading={uploading}
              />
            </Stack>
          </>
        )}

        {showApprovalForm && (
          <McaApprovalForm
            request={request}
            process={process}
            loading={loading}
            uploading={uploading}
            canUpload={canUploadHere}
            onUploadField={onUploadField}
            onApprove={onApprove}
            onReject={onReject}
            onTerminate={onTerminate}
          />
        )}

        {isCurrentStep && isActive && !canApprove && !stepResult?.actionAt && (
          <Text size="sm" color="dimmed" mt="sm">
            This step is awaiting approval.
          </Text>
        )}
      </Stack>
    </Paper>
  );
};

export default McaStepDetail;
